export type ReviewStatus = 'Unreviewed' | 'Reviewed' | 'ReviewRejected' | 'Reset' | 'ApprovedOvertimeRejected' | null;
export type ApprovalStatus = 'Approved' | 'ApprovalRejected' | 'Reset' | 'ReviewRejected' | 'ApprovedOvertimeRejected' | null;

export function normalizeStatuses(reviewStatusValue: any, approvalStatusValue: any): {
  reviewStatus: ReviewStatus;
  approvalStatus: ApprovalStatus;
} {
  if (reviewStatusValue === 'Approved' || reviewStatusValue === 'ApprovalRejected' || reviewStatusValue === 'ApprovedOvertimeRejected') {
    return {
      reviewStatus: 'Reviewed',
      approvalStatus: reviewStatusValue as ApprovalStatus
    };
  }

  if (reviewStatusValue === 'ReviewRejected') {
    return {
      reviewStatus: 'ReviewRejected',
      approvalStatus: reviewStatusValue as ApprovalStatus
    };
  }

  // Si ReviewStatusValue es 'Reset' y no hay approvalStatus, se interpreta como re-evaluar
  if (reviewStatusValue === 'Reset' && !approvalStatusValue) {
    return {
      reviewStatus: 'Reviewed',
      approvalStatus: 'Reset'
    };
  }

  return {
    reviewStatus: reviewStatusValue as ReviewStatus,
    approvalStatus: approvalStatusValue as ApprovalStatus
  };
}

export interface BadgeConfig {
  label: string;
  cssClass: string;
}

export interface RowPermissions {
  canApprove: boolean;
  canReject: boolean;
  canReset: boolean;
  canCorrect: boolean;
  canCorrectReviewed: boolean;
  isReadonlyExtraTimePay: boolean;
  correctAction: 'sst-edit' | 'dir-edit' | null;
}

export interface ReviewPayload {
  Action: 'Reviewed' | 'ReviewRejected' | 'Approved' | 'ApprovalRejected' | 'Reset' | 'Unreviewed' | 'ApprovedOvertimeRejected';
  Comment: string;
  Items: ReviewItem[];
}

export interface ReviewItem {
  PayrollPersonId: number;
  ExtraTimePay: number | null;
  /** Observación del cambio de estado (obligatoria al rechazar, rechazo final y re-evaluar). */
  Comment: string;
  /** Descripción o actividad realizada; se guarda aparte del comentario del estado. */
  ActivityDescription?: string;
}

/**
 * Entrada individual del historial de revisión de una fila de prenómina (ReviewHistory).
 * Entregado en GET api/PersonalReport/MemorandumPlaceOfWork.
 */
export interface ReviewHistoryEntry {
  Date?: string;                // fecha-hora de la acción, hora local del servidor, sin zona
  UpdateDate?: string;          // compatibilidad
  UserName: string | null;     // quien ejecutó la acción
  Action?: string | null;       // acción exacta enviada (Reviewed, ApprovalRejected, etc.)
  StatusValue?: string | null;  // estado en que quedó la fila
  StatusDescription?: string | null; // texto del estado, listo para mostrar
  Comment: string | null;      // comentario de esa acción (puede ser null)
}

/**
 * Movimiento del historial de estados de un PayrollPerson (compatibilidad hacia atrás).
 */
export interface PayrollPersonHistoryItem {
  Id?: number;
  ReviewStatusValue?: string;
  ReviewStatusDescription?: string;
  Comment: string | null;
  UserName: string | null;
  UpdateDate?: string;
  Date?: string;
  StatusValue?: string;
  StatusDescription?: string;
  Action?: string;
  ChangeType?: string;
}

const MESES_CORTOS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

/** Formatea la fecha de un cambio de estado como "29 Sep 2026 · 2:07 p. m.".
 * La fecha se formatea como hora local directa sin conversión desde UTC.
 */
export function formatStatusDate(value: string | Date | null | undefined): string {
  if (!value) { return ''; }
  if (typeof value === 'string') {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/);
    if (match) {
      const year = match[1];
      const monthIdx = parseInt(match[2], 10) - 1;
      const day = parseInt(match[3], 10);
      const hours = match[4] !== undefined ? parseInt(match[4], 10) : 0;
      const minutes = match[5] || '00';
      const h12 = hours % 12 === 0 ? 12 : hours % 12;
      const period = hours >= 12 ? 'p. m.' : 'a. m.';
      const mes = MESES_CORTOS[monthIdx] || match[2];
      if (match[4] !== undefined) {
        return `${day} ${mes} ${year} · ${h12}:${minutes} ${period}`;
      }
      return `${day} ${mes} ${year}`;
    }
  }
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) { return ''; }
  const hours = date.getHours();
  const h12 = hours % 12 === 0 ? 12 : hours % 12;
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const period = hours >= 12 ? 'p. m.' : 'a. m.';
  return `${date.getDate()} ${MESES_CORTOS[date.getMonth()]} ${date.getFullYear()} · ${h12}:${minutes} ${period}`;
}

/** Etiqueta y clase de color para un estado guardado en el historial. */
export function resolveHistoryBadge(
  statusValue?: string | null,
  action?: string | null,
  description?: string | null
): BadgeConfig {
  const desc = (description || '').toLowerCase();
  const act = (action || '').trim();
  const val = (statusValue || '').trim();

  // 1. Rechazos (SST, Director o Final) → siempre rojo (badge-rechazado)
  if (
    act === 'ReviewRejected' || act === 'ApprovalRejected' || act === 'ApprovedOvertimeRejected' ||
    val === 'ReviewRejected' || val === 'ApprovalRejected' || val === 'ApprovedOvertimeRejected' ||
    act.toLowerCase().includes('reject') || val.toLowerCase().includes('reject') ||
    desc.includes('rechaz')
  ) {
    let label = description;
    if (!label) {
      if (act === 'ApprovedOvertimeRejected' || val === 'ApprovedOvertimeRejected') {
        label = 'Rechazo final';
      } else if (act === 'ApprovalRejected' || val === 'ApprovalRejected') {
        label = 'Aprobación rechazada';
      } else {
        label = 'Rechazado';
      }
    }
    return { label, cssClass: 'badge-rechazado' };
  }

  // 2. Reinicio / Devolver a SST / Re-evaluar → siempre naranja (badge-reevaluar)
  if (
    act === 'Reset' || val === 'Reset' || act.toLowerCase().includes('reset') || val.toLowerCase().includes('reset') ||
    desc.includes('reinici') || desc.includes('re-eval') || desc.includes('reeval') || desc.includes('devolver')
  ) {
    return { label: description || 'Re-evaluar', cssClass: 'badge-reevaluar' };
  }

  // 3. Aprobado → azul (badge-aprobado)
  if (
    act === 'Approved' || val === 'Approved' ||
    (desc.includes('aprob') && !desc.includes('rechaz'))
  ) {
    return { label: description || 'Aprobado', cssClass: 'badge-aprobado' };
  }

  // 4. Revisado / Solicitado → verde (badge-revisado)
  if (
    act === 'Reviewed' || val === 'Reviewed' ||
    desc.includes('revisa') || desc.includes('solicit')
  ) {
    return { label: description || 'Revisado', cssClass: 'badge-revisado' };
  }

  // Fallback con normalización tradicional
  const primary = val || act;
  const normalized = normalizeStatuses(primary, null);
  const badge = resolveBadge(normalized.reviewStatus, normalized.approvalStatus);
  return {
    label: description || badge.label,
    cssClass: badge.cssClass
  };
}

export interface ApprovePayload {
  PayrollPersonIds: number[];
  Action: 'Approved' | 'ApprovalRejected' | 'Reset';
  Comment: string;
}

export function resolveBadge(
  reviewStatus: ReviewStatus,
  approvalStatus: ApprovalStatus
): BadgeConfig {

  if (approvalStatus === 'Approved') {
    return { label: 'Aprobado', cssClass: 'badge-aprobado' };
  }

  if (approvalStatus === 'ApprovalRejected') {
    return { label: 'Rechazado', cssClass: 'badge-rechazado' };
  }

  if (approvalStatus === 'ApprovedOvertimeRejected') {
    return { label: 'Rechazo final', cssClass: 'badge-rechazado' };
  }

  if (approvalStatus === 'Reset') {
    return { label: 'Re-evaluar', cssClass: 'badge-reevaluar' };
  }

  if (reviewStatus === 'Reviewed') {
    return { label: 'Revisado', cssClass: 'badge-revisado' };
  }

  if (reviewStatus === 'ReviewRejected') {
    return { label: 'Rechazado', cssClass: 'badge-rechazado' };
  }

  if (reviewStatus === 'Reset') {
    return { label: 'Re-evaluar', cssClass: 'badge-reevaluar' };
  }

  return { label: 'Sin revisar', cssClass: 'badge-sin-revisar' };
}

export function resolveRowPermissions(
  reviewStatus: ReviewStatus,
  approvalStatus: ApprovalStatus,
  canReview: boolean,
  canApproveP: boolean,
): RowPermissions {

  const hasAnyPayrollPermission = canReview || canApproveP;

  if (approvalStatus === 'Approved') {
    return {
      canApprove: false,
      canReject: false,
      canReset: false,
      canCorrect: true,
      canCorrectReviewed: false,
      isReadonlyExtraTimePay: true,
      correctAction: 'dir-edit',
    };
  }

  if (approvalStatus === 'ApprovalRejected') {
    return {
      canApprove: false,
      canReject: false,
      canReset: false,
      canCorrect: false,
      canCorrectReviewed: false,
      isReadonlyExtraTimePay: true,
      correctAction: null,
    };
  }

  if (approvalStatus === 'ApprovedOvertimeRejected') {
    return {
      canApprove: true,
      canReject: false,
      canReset: true,
      canCorrect: false,
      canCorrectReviewed: false,
      isReadonlyExtraTimePay: true,
      correctAction: null,
    };
  }

  if (approvalStatus === 'Reset') {
    return {
      canApprove: false,
      canReject: false,
      canReset: false,
      canCorrect: true,
      canCorrectReviewed: false,
      isReadonlyExtraTimePay: true,
      correctAction: 'dir-edit',
    };
  }

  if (reviewStatus === 'Reviewed' && !approvalStatus) {
    if (canApproveP) {
      return {
        canApprove: true,
        canReject: true,
        canReset: true,
        canCorrect: false,
        canCorrectReviewed: false,
        isReadonlyExtraTimePay: false,
        correctAction: 'dir-edit',
      };
    }

    return {
      canApprove: true,
      canReject: true,
      canReset: false,
      canCorrect: false,
      canCorrectReviewed: false,
      isReadonlyExtraTimePay: false,
      correctAction: null,
    };
  }

  if (reviewStatus === 'ReviewRejected' && !approvalStatus) {
    return {
      canApprove: false,
      canReject: false,
      canReset: false,
      canCorrect: true,
      canCorrectReviewed: false,
      isReadonlyExtraTimePay: false,
      correctAction: 'sst-edit',
    };
  }

  return {
    canApprove: true,
    canReject: true,
    canReset: canApproveP,
    canCorrect: false,
    canCorrectReviewed: false,
    isReadonlyExtraTimePay: !hasAnyPayrollPermission,
    correctAction: null,
  };
}

export function requiresComment(
  action: 'Reviewed' | 'ReviewRejected' | 'Approved' | 'ApprovalRejected' | 'Reset' | 'Unreviewed' | 'ApprovedOvertimeRejected'
): boolean {
  return action === 'ReviewRejected'
    || action === 'ApprovalRejected'
    || action === 'Reset'
    || action === 'Unreviewed'
    || action === 'ApprovedOvertimeRejected';
}

export function buildReviewPayload(
  items: Array<{ payrollPersonId: number; extraTimePay: number | null; comment: string; activityDescription?: string }>,
  action: 'Reviewed' | 'ReviewRejected' | 'Approved' | 'ApprovalRejected' | 'Reset' | 'Unreviewed' | 'ApprovedOvertimeRejected',
  globalComment: string = ''
): ReviewPayload {
  return {
    Action: action,
    Comment: globalComment,
    Items: items.map(i => ({
      PayrollPersonId: i.payrollPersonId,
      ExtraTimePay: i.extraTimePay,
      Comment: i.comment,
      ActivityDescription: i.activityDescription ?? '',
    })),
  };
}
